// Email Verification Page
// Handles email verification process as per PRD Section 5.2

import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useEmailVerification, useSignOut } from '../hooks/useAuth';
import { getErrorMessage } from '../lib/firebase-errors';
import { FormButton, FormError, FormSuccess } from '../components/form';
import { DeleteAccountSection } from '../components/account/DeleteAccountSection';
import { isPRPreviewEnvironment } from '../lib/utils/environment';
import { validateReturnToPath } from '../lib/utils/navigation';

// How long Resend stays disabled after a send. A guard against a double
// press, chosen by us: Firebase does its own throttling server-side.
const RESEND_COOLDOWN_MS = 60_000;

export function VerifyEmailPage() {
  const { user, isEmailVerified } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const emailVerificationMutation = useEmailVerification();
  const signOutMutation = useSignOut();
  const [verificationSent, setVerificationSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-enable Resend after the cooldown. Without this it stayed disabled
  // until a reload, while the page tells someone who closed the hosted page
  // to "resend it below" (#2129).
  useEffect(() => {
    if (!verificationSent) {
      return;
    }
    const timer = setTimeout(
      () => setVerificationSent(false),
      RESEND_COOLDOWN_MS
    );
    return () => clearTimeout(timer);
  }, [verificationSent]);

  // Redirect if user is not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Redirect if email is already verified
  if (isEmailVerified) {
    // Extract and validate returnTo from location state. Keep the query
    // string: flows like /oidc/continue?request_id=... carry one-time state
    // in it.
    const state = location.state as { returnTo?: Location } | null;
    const returnToPath = state?.returnTo
      ? `${state.returnTo.pathname}${state.returnTo.search ?? ''}`
      : undefined;
    const validatedPath = validateReturnToPath(returnToPath);
    const destination = validatedPath || '/dashboard';

    // Forward returnTo state to next page (complete-profile)
    return (
      <Navigate
        to={destination}
        replace
        state={validatedPath ? { returnTo: state?.returnTo } : undefined}
      />
    );
  }

  /**
   * Navigates explicitly rather than letting `ProtectedRoute` bounce us.
   *
   * That bounce is `<Navigate to="/" state={{ returnTo: location }} />`, and
   * `location` here is `/verify-email` itself. The state survives through
   * sign-in and signup and comes back to this page, which would then compute
   * `/verify-email` as its own post-verification destination — a page
   * navigating to itself, carrying the value that caused it. Nothing produces
   * that shape today; adding sign-out is what would have.
   */
  const handleSignOut = async () => {
    try {
      setError(null);
      await signOutMutation.mutateAsync();
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleResendVerification = async () => {
    try {
      setError(null);
      setVerificationSent(false);
      await emailVerificationMutation.mutateAsync();
      setVerificationSent(true);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
            Verify Your Email
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            We sent a verification link to:
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900 bg-gray-100 rounded-md px-3 py-1 inline-block">
            {user.email}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Follow these steps:
              </h3>
              <ol className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                    1
                  </span>
                  {isPRPreviewEnvironment() ? (
                    <span className="text-amber-600">
                      Email verification unavailable in PR preview
                    </span>
                  ) : (
                    'Check your email inbox (and spam folder)'
                  )}
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                    2
                  </span>
                  Open the link in the email. If the page asks you to confirm,
                  press its confirmation button. Your address is verified only
                  once that page says so.
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                    3
                  </span>
                  Then press Continue on that page to carry on. If you come back
                  to this tab instead, refresh it.
                </li>
              </ol>
              <p className="mt-3 text-sm text-gray-600">
                Closed that page before pressing the button? Open the link in
                the email again, or resend it below.
              </p>
            </div>

            {verificationSent && (
              <FormSuccess>
                Verification email sent! Please check your inbox. You can send
                another in a minute.
              </FormSuccess>
            )}

            {error && <FormError>{error}</FormError>}

            <div className="space-y-4">
              <FormButton
                onClick={handleResendVerification}
                isLoading={emailVerificationMutation.isPending}
                disabled={verificationSent || isPRPreviewEnvironment()}
                loadingText="Sending Email..."
                variant={
                  isPRPreviewEnvironment()
                    ? 'secondary'
                    : verificationSent
                      ? 'secondary'
                      : 'primary'
                }
                title={
                  isPRPreviewEnvironment() ? 'Not available in PR preview' : ''
                }
              >
                {isPRPreviewEnvironment()
                  ? 'Unavailable in PR Preview'
                  : verificationSent
                    ? 'Email Sent'
                    : 'Resend Verification Email'}
              </FormButton>

              {isPRPreviewEnvironment() && (
                <p className="text-sm text-center text-amber-600 mt-2">
                  To test email verification, use{' '}
                  <a
                    href="https://dev.heapster.id"
                    className="underline font-medium"
                  >
                    dev.heapster.id
                  </a>
                </p>
              )}

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Having trouble?{' '}
                  <Link
                    to="/contact"
                    className="font-medium text-sky-600 hover:text-sky-500 transition-colors duration-200"
                  >
                    Contact Support
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/*
            This said "Wrong email address? Update your email" and linked to
            /profile/edit, which is not a route — and there is no email-change
            feature anywhere in the product for it to have linked to. Since the
            only page carrying a sign-out control was /dashboard, which an
            unverified user is redirected away from, someone who mistyped their
            address had no way out of this page at all.

            Signing out and signing up again is the honest instruction, and it
            is now possible. On its own it leaves the mistyped address
            registered to an abandoned account — a consequence ADR-016 accepted
            deliberately, naming "a self-service 'cancel this signup' that
            deletes the account and frees the address" as the right answer when
            one existed. It now does, so it is offered here.

            This is also the only route out for an unverified account. /settings
            is wrapped in <VerifiedRoute>, so the Danger Zone carrying the same
            control is unreachable for exactly the people most likely to want
            it: someone who mistyped their address can never verify it, and
            could not previously delete the account either (#1920).
          */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Wrong email address? Sign out and create your account again with
                the right one — that leaves this address registered here. To
                free it as well, delete this account below.
              </p>
              <FormButton
                type="button"
                variant="secondary"
                onClick={() => void handleSignOut()}
                disabled={signOutMutation.isPending}
                className="mt-3"
              >
                {signOutMutation.isPending ? 'Signing out…' : 'Sign out'}
              </FormButton>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <DeleteAccountSection
              heading="Delete this account"
              description="Removes the account and frees this email address so it can be used to sign up again. This cannot be undone."
              actionLabel="Delete this account"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
